import type { KeyboardEvent } from "react";
import { useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { StatusPill } from "./StatusPill.tsx";
import { LivePreview } from "./LivePreview.tsx";
import type { Camera } from "../../api/flashbackApi/types.ts";
import React from "react";

interface Props {
  camera: Camera;
  fillCell?: boolean;
  onToggleStatus: (camera: Camera) => void;
  onOpenPlayback: (camera: Camera) => void;
}

export function CameraCard({
  camera,
  fillCell = false,
  onToggleStatus,
  onOpenPlayback,
}: Props): React.ReactElement {
  const isOnline = camera.status === "active";
  const isRecordingActive = camera.manualRecordingStartedAt !== null;

  const openPlayback = useCallback((): void => {
    onOpenPlayback(camera);
  }, [camera, onOpenPlayback]);

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    openPlayback();
  };

  return (
    <Box
      component="article"
      role="button"
      tabIndex={0}
      onClick={openPlayback}
      onKeyDown={handleKeyDown}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: fillCell ? "100%" : "auto",
        minHeight: 0,
        overflow: "hidden",
        borderRadius: "14px",
        border: "1px solid rgb(229, 231, 235)",
        backgroundColor: "#FFFFFF",
        cursor: "pointer",
        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
        transition:
          "box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease",
        "&:hover": {
          borderColor: "rgb(229, 231, 235)",
          boxShadow: "0 8px 16px rgba(0, 0, 0, 0.12)",
          transform: "translateY(-4px) scale(1.02)",
        },
        "&:focus-visible": {
          outline: "none",
          boxShadow: "0 0 0 2px rgba(41, 50, 229, 0.35)",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          backgroundColor: "var(--app-bg)",
          ...(fillCell
            ? { flex: 1, minHeight: 0 }
            : { aspectRatio: "16 / 9", flexShrink: 0 }),
        }}
      >
        <LivePreview cameraId={camera.id} status={camera.status} />

        <Box sx={{ position: "absolute", left: 8, top: 8, zIndex: 10 }}>
          <StatusPill status={camera.status} />
        </Box>
        {isRecordingActive ? (
          <Box
            role="status"
            aria-label="Recording active"
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              zIndex: 10,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.25,
              borderRadius: "999px",
              backgroundColor: "#DC2626",
              color: "#FFFFFF",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              boxShadow: "0 1px 4px rgba(0, 0, 0, 0.28)",
            }}
          >
            <Box
              component="span"
              aria-hidden
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: "#FFFFFF",
              }}
            />
            Rec
          </Box>
        ) : null}
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          p: 1.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography
            variant="body2"
            component="h3"
            title={camera.name}
            sx={{
              fontWeight: 600,
              color: "var(--app-text-primary)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
              minWidth: 0,
            }}
          >
            {camera.name}
          </Typography>
          <Box
            component="button"
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onToggleStatus(camera);
            }}
            sx={{
              flexShrink: 0,
              borderRadius: "4px",
              border: "1px solid var(--app-border)",
              px: 1,
              py: 0.25,
              fontSize: 11,
              lineHeight: 1.2,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: "var(--app-text-secondary)",
              backgroundColor: "var(--app-surface)",
              cursor: "pointer",
              "&:hover": {
                borderColor: "var(--app-primary)",
                color: "var(--app-text-primary)",
              },
              "&:focus-visible": {
                outline: "none",
                boxShadow: "0 0 0 2px rgba(41, 50, 229, 0.35)",
              },
            }}
          >
            {isOnline ? "Disable" : "Enable"}
          </Box>
        </Box>

        <Box
          component="dl"
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            columnGap: 1.5,
            rowGap: 0.5,
            m: 0,
            fontSize: 12,
          }}
        >
          <Meta label="Group" value={camera.group?.name ?? "—"} />
          <Meta label="Type" value={camera.type} />
          <Meta label="Mode" value={camera.recordingMode} />
        </Box>
      </Box>
    </Box>
  );
}

interface MetaProps {
  label: string;
  value: string;
}

function Meta({ label, value }: MetaProps): React.ReactElement {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      <Typography
        component="dt"
        variant="caption"
        sx={{ color: "var(--app-text-secondary)", flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Typography
        component="dd"
        variant="caption"
        sx={{
          m: 0,
          fontWeight: 500,
          color: "var(--app-text-primary)",
          textTransform: "capitalize",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}
